import { refreshAsync } from 'expo-auth-session';

import { config } from '../../config';
import { checkIfAccessTokenHasExpired } from '../check-if-access-token-has-expired';
import { getHybridFlowApiAccessToken } from '../get-api-access-token';
import { getHybridFlowRefreshToken } from '../get-refresh-token';
import { storeHybridFlowTokens } from '../store-tokens';
import { logger } from './refresh-api-access-token.logger';

/**
 * Retrieves fresh access token for our APIs, using the locally stored refresh token.
 *
 * @see https://docs.expo.dev/versions/latest/sdk/auth-session/#authsessionfetchdiscoveryasyncissuer
 * @see https://docs.expo.dev/versions/latest/sdk/auth-session/#authsessionrefreshasyncconfig-discovery
 */
export async function refreshHybridFlowApiAccessToken(): Promise<string | undefined | null> {
  try {
    const currentRefreshToken = await getHybridFlowRefreshToken();

    if (!currentRefreshToken) {
      return;
    }

    // ─── Re-use the current access token if it's not expired ─────────────

    const currentApiAccessToken = await getHybridFlowApiAccessToken();

    const isCurrentTokenExpired = currentApiAccessToken
      ? checkIfAccessTokenHasExpired({
          accessToken: currentApiAccessToken,
          safeGuardMinutes: 5,
        })
      : true;

    if (!isCurrentTokenExpired) {
      return currentApiAccessToken;
    }

    // ─── Get a new token ─────────────────────────────────────────────────

    const { clientId, scopes, discovery } = config;
    const apiLoginRequestScope = scopes.apiLoginRequest.scopes;

    const request = { clientId, refreshToken: currentRefreshToken };
    const apiAccessTokenRequest = { ...request, scopes: apiLoginRequestScope };

    const { accessToken: apiAccessToken, refreshToken } = await refreshAsync(
      apiAccessTokenRequest,
      discovery,
    );

    await storeHybridFlowTokens({ accessToken: apiAccessToken, refreshToken });

    return apiAccessToken;
  } catch (error) {
    logger.error(error);

    return null;
  }
}
